import {
  DOCUMENT_ICON,
  FOLDER_ICON,
  RESEARCH_ICON,
  STRUCTURED_DOCUMENT_ICON,
} from "@/components/display/display-icon/artifacts";
import { Button } from "@/components/ui/button";
import { useDocumentsData } from "@/data/use-documents-data";
import { useGroupFoldersData } from "@/data/use-group-folder-data";
import { useGroupRepositoryKitData } from "@/data/use-group-repository-kit-data";
import { useKitFoldersData } from "@/data/use-kit-folders-data";
import { useKitData } from "@/data/use-kits-data";
import { useSettingData } from "@/data/use-settings-data";
import { useGroupAndKitTemplatesData } from "@/data/use-templates-data";
import { useArtifactUuid } from "@/hooks/use-artifact-uuid";
import { useGroupSlug } from "@/hooks/use-group-slug";
import { getKitKnowledgeRootFolders } from "@/lib/folder";
import { canOpenGrant, isOwnedArtifact } from "@/lib/grant";
import { Folder } from "@/types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAdd,
  faBooks,
  faCloudPlus,
  faEye,
  faGear,
  faImages,
  faUnlink,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useMatchRoute, useNavigate } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { KitMenuTemplateProps } from "../KitMenuTemplate";

export const SETTINGS_GROUP_PARTIAL = {
  title: "Settings",
  icon: faGear,
};

export const useDocumentGroup = ({
  groupUuid,
  groupSlug,
  kitUuid,
  onCreateResearchClick,
}: {
  groupUuid: string;
  groupSlug: string;
  kitUuid: string;
  onCreateResearchClick?: () => void;
}): KitMenuTemplateProps["groups"][number] => {
  const navigate = useNavigate();
  const { createDocument } = useDocumentsData(groupUuid, kitUuid);
  const { documents, reorderDocuments } = useDocumentsData(groupUuid, kitUuid);
  const documentUuid = useArtifactUuid("documentUuid");
  const { linkTemplate } = useKitData(groupUuid, kitUuid);
  const { kitTemplates, remainingGroupTemplates, allTemplates } =
    useGroupAndKitTemplatesData({ groupUuid, kitUuid });
  const { value: researcherFeatureFlag } = useSettingData(
    groupUuid,
    "FEATURE_FLAG_RESEARCHER",
  );

  const handleCreateBlankDocument = async () => {
    const document = await createDocument({});
    if (document) {
      navigate({
        to: "/w/$groupSlug/k/$kitUuid/document/$documentUuid",
        params: { groupSlug, kitUuid, documentUuid: document.uuid },
      });
    }
  };

  const handleCreateDocumentFromTemplate = async (templateUuid: string) => {
    const templateName = allTemplates.find(
      (t) => t.uuid === templateUuid,
    )?.name;
    const document = await createDocument({
      name: templateName,
      templateUuid,
    });
    if (document) {
      navigate({
        to: "/w/$groupSlug/k/$kitUuid/document/$documentUuid",
        params: { groupSlug, kitUuid, documentUuid: document.uuid },
      });
    }
  };

  const handleCreateDocumentFromExternalTemplate = async (
    templateUuid: string,
  ) => {
    await linkTemplate({ templateUuid });
    await handleCreateDocumentFromTemplate(templateUuid);
  };

  const handleReorderDocuments = async (uuids: string[]) => {
    await reorderDocuments({ uuids });
  };

  return {
    title: "Document",
    icon: DOCUMENT_ICON,
    onOrderChange: handleReorderDocuments,
    showCounter: true,
    items: documents.map((d) => ({
      id: d.uuid,
      title: d.name,
      icon: d.type === "structured" ? STRUCTURED_DOCUMENT_ICON : DOCUMENT_ICON,
      link: {
        to: "/w/$groupSlug/k/$kitUuid/document/$documentUuid" as const,
        params: { groupSlug, kitUuid, documentUuid: d.uuid },
      },
      active: d.uuid === documentUuid,
    })),
    menu: [
      {
        title: "Create Document",
        icon: faAdd,
        items: [
          {
            id: "blank-document",
            title: "Blank Document",
            icon: DOCUMENT_ICON,
            onClick: handleCreateBlankDocument,
          },
          ...(researcherFeatureFlag === "enabled"
            ? [
                {
                  id: "research-document",
                  title: "Research Document",
                  icon: RESEARCH_ICON,
                  onClick: () => onCreateResearchClick?.(),
                },
              ]
            : []),
          {
            id: "separator",
            separator: kitTemplates.length > 0,
            title: "Workbook Templates",
          },
          ...kitTemplates.map((template) => ({
            id: template.uuid,
            title: template.name,
            icon: STRUCTURED_DOCUMENT_ICON,
            onClick: () => handleCreateDocumentFromTemplate(template.uuid),
          })),
          {
            id: "separator",
            separator: remainingGroupTemplates.length > 0,
            title: "Workspace Templates",
          },
          ...remainingGroupTemplates.map((template) => ({
            id: template.uuid,
            title: template.name,
            icon: STRUCTURED_DOCUMENT_ICON,
            onClick: () =>
              handleCreateDocumentFromExternalTemplate(template.uuid),
          })),
        ],
      },
    ],
  };
};

export const useFolderGroup = ({
  groupUuid,
  kitUuid,
  onLinkDriveFolderClick,
  excludeFolderUuids,
  itemDropdownMenu,
}: {
  groupUuid: string;
  kitUuid: string;
  onLinkDriveFolderClick: () => void;
  excludeFolderUuids?: string[];
  itemDropdownMenu?: (folderUuid: string) => ReactElement[];
}): KitMenuTemplateProps["groups"][number] => {
  const navigate = useNavigate();
  const groupSlug = useGroupSlug();
  const matchRoute = useMatchRoute();

  // Data
  const { linkFolder, kit } = useKitData(groupUuid, kitUuid);
  const { folders, createFolder } = useKitFoldersData(groupUuid, kitUuid);
  const { folders: groupFolders } = useGroupFoldersData({
    groupUuid,
    access: kit?.type === "collection" ? "owner" : undefined,
  });
  const { isLoading: isLoadingGroupRepositoryKit, kit: groupRepositoryKit } =
    useGroupRepositoryKitData(groupUuid);

  const isGroupRepositoryKit = groupRepositoryKit?.uuid === kitUuid;
  const canLinkConnectorFolder = isGroupRepositoryKit || kit?.type === "expert";
  // Kit-linked folders are knowledge roots even when nested under Expert.folder_uuid
  // for RAG (parent exists but is outside the kit folder set).
  const excluded = new Set(excludeFolderUuids ?? []);
  const rootFolders = getKitKnowledgeRootFolders(folders).filter(
    (f) => !excluded.has(f.uuid),
  );
  const remainingGroupFolders = groupFolders.filter((wf) =>
    folders.every((f) => f.uuid !== wf.uuid),
  );
  const showGroupFolders =
    !isLoadingGroupRepositoryKit && !isGroupRepositoryKit;

  const handleCreateFolder = async () => {
    const folder = await createFolder({ name: "Untitled Folder" });
    if (folder) {
      navigate({
        to: "/w/$groupSlug/k/$kitUuid/knowledge/$folderUuid/$",
        params: { groupSlug, kitUuid, folderUuid: folder.uuid, _splat: "" },
      });
    }
  };

  const handleAddGroupFolder = async (folder: Folder) => {
    await linkFolder({ folderUuid: folder.uuid });
    navigate({
      to: "/w/$groupSlug/k/$kitUuid/knowledge/$folderUuid/$",
      params: { groupSlug, kitUuid, folderUuid: folder.uuid, _splat: "" },
    });
  };

  const getGroupFolderPrefix = (folder: (typeof folders)[number] | Folder) => {
    if ("readonly" in folder && isOwnedArtifact(folder)) {
      return `${kit?.name}/`;
    } else if (folder.group?.uuid === groupUuid) {
      return `${folder.group.name}/`;
    } else {
      return `${folder.group?.name}/`;
    }
  };

  const menu: NonNullable<KitMenuTemplateProps["groups"][number]["menu"]> = [
    {
      title: "View All",
      icon: faEye,
      link: {
        to: "/w/$groupSlug/k/$kitUuid/knowledge" as const,
        params: { groupSlug, kitUuid },
      },
    },
    {
      title: "Add Folder",
      icon: faAdd,
      items: [
        {
          id: "create-folder",
          title: "Create Folder",
          icon: faAdd,
          onClick: handleCreateFolder,
        },
        ...(canLinkConnectorFolder
          ? [
              {
                id: "link-connector-folder",
                title: "Link Connector Folder",
                icon: faCloudPlus,
                onClick: onLinkDriveFolderClick,
              },
            ]
          : []),
        ...(showGroupFolders
          ? remainingGroupFolders.map((wf) => {
              return {
                id: wf.uuid,
                title: wf.name,
                prefix: getGroupFolderPrefix(wf),
                icon: FOLDER_ICON,
                onClick: () => {
                  handleAddGroupFolder(wf);
                },
              };
            })
          : []),
      ],
    },
  ]!;

  return {
    title: "Knowledge",
    icon: faBooks,
    active: !!matchRoute({
      to: "/w/$groupSlug/k/$kitUuid/knowledge",
      params: { groupSlug, kitUuid },
    }),
    showCounter: true,
    items: rootFolders.map((f) => {
      return {
        id: f.uuid,
        title: f.name,
        prefix: getGroupFolderPrefix(f),
        icon: FOLDER_ICON,
        disabled: !canOpenGrant(f.access),
        link: {
          to: "/w/$groupSlug/k/$kitUuid/knowledge/$folderUuid" as const,
          params: { groupSlug, kitUuid, folderUuid: f.uuid },
        },
        active: !!matchRoute({
          to: "/w/$groupSlug/k/$kitUuid/knowledge/$folderUuid",
          params: { groupSlug, kitUuid, folderUuid: f.uuid },
          fuzzy: true,
        }),
        dropdownMenu: itemDropdownMenu?.(f.uuid),
      };
    }),
    menu,
  };
};

export const useExpertKnowledgeGroup = ({
  groupUuid,
  kitUuid,
  expertFolderUuids,
  onAssociate,
  onDissociate,
}: {
  groupUuid: string;
  kitUuid: string;
  expertFolderUuids: string[];
  onAssociate: (folderUuid: string) => Promise<unknown>;
  onDissociate: (folderUuid: string) => Promise<unknown>;
}): KitMenuTemplateProps["groups"][number] => {
  const navigate = useNavigate();
  const groupSlug = useGroupSlug();
  const matchRoute = useMatchRoute();
  const { kit } = useKitData(groupUuid, kitUuid);
  const { folders, createFolder } = useKitFoldersData(groupUuid, kitUuid);

  const associatedUuids = new Set(expertFolderUuids);
  const rootFolders = folders.filter((f) => !f.parentFolderUuid);
  const associatedFolders = rootFolders.filter((f) =>
    associatedUuids.has(f.uuid),
  );
  const remainingFolders = rootFolders.filter(
    (f) => !associatedUuids.has(f.uuid),
  );

  const handleCreateFolder = async () => {
    const folder = await createFolder({ name: "Untitled Folder" });
    if (folder) {
      await onAssociate(folder.uuid);
      navigate({
        to: "/w/$groupSlug/k/$kitUuid/knowledge/$folderUuid/$",
        params: { groupSlug, kitUuid, folderUuid: folder.uuid, _splat: "" },
      });
    }
  };

  const handleAssociate = async (folderUuid: string) => {
    await onAssociate(folderUuid);
    navigate({
      to: "/w/$groupSlug/k/$kitUuid/knowledge/$folderUuid/$",
      params: { groupSlug, kitUuid, folderUuid, _splat: "" },
    });
  };

  return {
    title: "Expert Knowledge",
    icon: faBooks,
    active:
      !!matchRoute({
        to: "/w/$groupSlug/k/$kitUuid/expert-knowledge",
        params: { groupSlug, kitUuid },
      }) ||
      associatedFolders.some((f) =>
        matchRoute({
          to: "/w/$groupSlug/k/$kitUuid/knowledge/$folderUuid",
          params: { groupSlug, kitUuid, folderUuid: f.uuid },
          fuzzy: true,
        }),
      ),
    showCounter: true,
    items: associatedFolders.map((f) => ({
      id: f.uuid,
      title: f.name,
      prefix: kit?.name ? `${kit.name}/` : undefined,
      icon: FOLDER_ICON,
      disabled: !canOpenGrant(f.access),
      link: {
        to: "/w/$groupSlug/k/$kitUuid/knowledge/$folderUuid" as const,
        params: { groupSlug, kitUuid, folderUuid: f.uuid },
      },
      active: !!matchRoute({
        to: "/w/$groupSlug/k/$kitUuid/knowledge/$folderUuid",
        params: { groupSlug, kitUuid, folderUuid: f.uuid },
        fuzzy: true,
      }),
      dropdownMenu: [
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => onDissociate(f.uuid)}
        >
          <FontAwesomeIcon icon={faUnlink} />
          Remove from Expert Knowledge
        </Button>,
      ],
    })),
    menu: [
      {
        title: "View All",
        icon: faEye,
        link: {
          to: "/w/$groupSlug/k/$kitUuid/expert-knowledge" as const,
          params: { groupSlug, kitUuid },
        },
      },
      {
        title: "Add Folder",
        icon: faAdd,
        items: [
          {
            id: "create-folder",
            title: "Create Folder",
            icon: faAdd,
            onClick: handleCreateFolder,
          },
          ...remainingFolders.map((folder) => ({
            id: folder.uuid,
            title: folder.name,
            icon: FOLDER_ICON,
            onClick: () => handleAssociate(folder.uuid),
          })),
        ],
      },
    ],
  };
};

export const useLibraryGroup = ({
  onOpen,
}: {
  onOpen: () => void;
}): KitMenuTemplateProps["groups"][number] => {
  return {
    title: "Library",
    icon: faImages,
    onClick: onOpen,
  };
};
